import React, { useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { MessageSquareWarning, FileWarning, Lightbulb, AlertTriangle, Send, ChevronLeft } from 'lucide-react';

const reportTypes = [
  { key: 'feedback', label: 'Feedback / Complaint / Demand', icon: MessageSquareWarning, color: 'gold', desc: 'Share customer feedback, complaints, or demands' },
  { key: 'incident', label: 'Incident Report', icon: FileWarning, color: 'red-400', desc: 'Report an incident that occurred on premises' },
  { key: 'improvement', label: 'Improvement Advice', icon: Lightbulb, color: 'emerald-400', desc: 'Suggest improvements for operations or service' },
  { key: 'urgent', label: 'Urgent Report', icon: AlertTriangle, color: 'amber-400', desc: 'Flag an urgent issue requiring immediate attention' },
];

const priorityOptions = [
  { value: 'low', label: 'Low', cls: 'text-zinc-400' },
  { value: 'medium', label: 'Medium', cls: 'text-blue-400' },
  { value: 'high', label: 'High', cls: 'text-amber-400' },
  { value: 'urgent', label: 'Urgent', cls: 'text-red-400' },
];

const feedbackCategories = ['Customer Feedback', 'Customer Complaint', 'Customer Demand', 'Staff Feedback', 'Other'];
const improvementAreas = ['Operations', 'Menu', 'Service', 'Facility', 'Technology', 'Staff Training', 'Other'];

export default function QuickLinksPage() {
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', priority: 'medium', extra_fields: {} });

  const resetForm = () => { setForm({ subject: '', description: '', priority: 'medium', extra_fields: {} }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSubmitting(true);
    try {
      await api.post('/quick-reports', { report_type: selected, ...form });
      toast.success('Report submitted successfully! Management has been notified.');
      resetForm();
      setSelected(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const setExtra = (key, val) => setForm(p => ({ ...p, extra_fields: { ...p.extra_fields, [key]: val } }));

  if (!selected) {
    return (
      <div className="animate-fadeIn max-w-2xl mx-auto">
        <p className="text-sm font-sans text-zinc-400 mb-6">Select a form to submit</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {reportTypes.map(rt => {
            const Icon = rt.icon;
            return (
              <button
                key={rt.key}
                onClick={() => { resetForm(); setSelected(rt.key); }}
                className={`p-6 bg-white/[0.03] border border-white/5 rounded-sm card-hover text-left group transition-all hover:border-${rt.color}/30`}
                data-testid={`quick-link-${rt.key}`}
              >
                <div className={`w-10 h-10 rounded-sm bg-${rt.color}/10 flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 text-${rt.color}`} />
                </div>
                <h3 className="text-white font-sans font-medium text-sm mb-1">{rt.label}</h3>
                <p className="text-zinc-500 font-sans text-xs">{rt.desc}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const activeType = reportTypes.find(r => r.key === selected);
  const ActiveIcon = activeType.icon;

  return (
    <div className="animate-fadeIn max-w-xl mx-auto">
      <button onClick={() => { setSelected(null); resetForm(); }} className="flex items-center gap-1 text-zinc-500 hover:text-zinc-300 font-sans text-sm mb-4 transition-colors" data-testid="quick-links-back">
        <ChevronLeft className="w-4 h-4" /> Back to Quick Links
      </button>

      <div className={`flex items-center gap-3 mb-6 p-4 bg-white/[0.03] border border-white/5 rounded-sm`}>
        <div className={`w-10 h-10 rounded-sm bg-${activeType.color}/10 flex items-center justify-center`}>
          <ActiveIcon className={`w-5 h-5 text-${activeType.color}`} />
        </div>
        <div>
          <h2 className="text-white font-sans font-medium">{activeType.label}</h2>
          <p className="text-zinc-500 font-sans text-xs">{activeType.desc}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid={`quick-report-form-${selected}`}>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Subject</Label>
          <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Brief subject line" data-testid="report-subject" />
        </div>

        {selected === 'feedback' && (
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Category</Label>
            <Select value={form.extra_fields.category || ''} onValueChange={v => setExtra('category', v)}>
              <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="report-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="bg-bg-secondary border border-white/10">
                {feedbackCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {selected === 'incident' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Date of Incident</Label>
                <Input type="date" value={form.extra_fields.incident_date || ''} onChange={e => setExtra('incident_date', e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" data-testid="report-incident-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Location</Label>
                <Input value={form.extra_fields.location || ''} onChange={e => setExtra('location', e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Where it happened" data-testid="report-location" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">People Involved</Label>
              <Input value={form.extra_fields.people_involved || ''} onChange={e => setExtra('people_involved', e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Names of people involved" data-testid="report-people" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Witnesses</Label>
              <Input value={form.extra_fields.witnesses || ''} onChange={e => setExtra('witnesses', e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Names of witnesses" data-testid="report-witnesses" />
            </div>
          </>
        )}

        {selected === 'improvement' && (
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Area</Label>
            <Select value={form.extra_fields.area || ''} onValueChange={v => setExtra('area', v)}>
              <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="report-area"><SelectValue placeholder="Select area" /></SelectTrigger>
              <SelectContent className="bg-bg-secondary border border-white/10">
                {improvementAreas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Description *</Label>
          <textarea
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            className="w-full bg-transparent border border-white/10 text-white font-sans text-sm rounded-sm p-3 min-h-[120px] focus:border-gold/50 focus:outline-none focus:ring-0 resize-none"
            placeholder="Provide detailed description..."
            required
            data-testid="report-description"
          />
        </div>

        {selected !== 'urgent' && (
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Priority</Label>
            <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
              <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="report-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-bg-secondary border border-white/10">
                {priorityOptions.map(p => <SelectItem key={p.value} value={p.value}><span className={p.cls}>{p.label}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {selected === 'urgent' && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
            <p className="text-red-400 font-sans text-xs flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> This report will be flagged as URGENT and management will be notified immediately
            </p>
          </div>
        )}

        {selected === 'improvement' && (
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Expected Benefit</Label>
            <Input value={form.extra_fields.expected_benefit || ''} onChange={e => setExtra('expected_benefit', e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="What benefit would this bring?" data-testid="report-benefit" />
          </div>
        )}

        <Button type="submit" disabled={submitting} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="report-submit">
          {submitting ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Submit Report</>}
        </Button>
      </form>
    </div>
  );
}
